import { computed, makeObservable } from 'mobx'
import {
  DocumentsQuestionDTO,
  DocumentsQuestionResponseDTO,
} from 'jobs/dtos/questions'
import DocumentRequestsViewModel, {
  RequestedDocumentDescriptor,
} from 'documents/components/DocumentRequests/DocumentRequestsViewModel'
import { truncateTextItems } from 'utils/string-util'
import QuestionViewModelBase, {
  QuestionViewModelPort,
} from '../QuestionViewModelBase'
import { QuestionInputValue } from '../input-value-types'
import { QuestionValidationResult } from '../QuestionValidationResult'

export interface DocumentsValidation {
  minDocsRequired: number
}

export interface DocumentsSettings {
  showErrorMessages: boolean
  readOnly: boolean
}

export const DocumentsSettings = {
  defaults: {
    showErrorMessages: false,
    readOnly: false,
  } as DocumentsSettings,
}

export default class DocumentsQuestionViewModel extends QuestionViewModelBase<DocumentsQuestionDTO> {
  // Not used.
  answer?: unknown
  /**
   * Document requests.
   */
  documentRequests = new DocumentRequestsViewModel(
    this.adapter.documentRequestsAdapterFactory(),
  )

  /**
   * Propagate changes from the underlying VM to the questions VM.
   */
  private onChangeSub = this.documentRequests.changes$.subscribe(this.notify)

  validations?: DocumentsValidation
  settings: DocumentsSettings = DocumentsSettings.defaults

  constructor(private documentsQuestionView: QuestionViewModelPort) {
    super(documentsQuestionView)

    makeObservable(this)
  }

  /**
   * Subset of document requests that were provided.
   */
  @computed
  get providedDocumentRequests() {
    return this.documentRequests.documentRequests.filter((d) => d.documentId)
  }

  @computed
  get isAnswered(): boolean {
    return this.providedDocumentRequests.length > 0
  }

  configure(settings: DocumentsSettings, validations: DocumentsValidation) {
    this.settings = settings
    this.validations = validations
    return this
  }

  protected textAnswer(maxLength: number): string {
    return truncateTextItems(
      this.providedDocumentRequests.map((s) => s.title),
      maxLength,
    )
  }

  protected hydrate(dto: DocumentsQuestionDTO) {
    this.documentRequests.reconcile(
      dto.options.map((option) => {
        return {
          id: option.id,
          title: option.text,
          documentId: dto.answer?.find((a) => a.id === option.id)?.document_id,
          documentTagIds: option.document_tags,
          supplemental: false,
        }
      }),
    )
  }

  protected serializeAnswer(): DocumentsQuestionResponseDTO[] {
    return this.providedDocumentRequests.map((vm) => {
      const descriptor = vm.toDescriptor()
      return {
        id: descriptor.id,
        document_id: descriptor.documentId!,
        supplemental: descriptor.supplemental || false,
        title: descriptor.title,
      }
    })
  }

  protected hydrateAnswer(answer: unknown): void {
    if (!Array.isArray(answer)) {
      return
    }

    const descriptors: RequestedDocumentDescriptor[] = []
    const optionValues = new Map(answer.map((a) => [a.id, a]))
    for (const descriptor of this.documentRequests.documentRequestDescriptors) {
      descriptors.push({
        ...descriptor,
        documentId: optionValues.get(descriptor.id)?.document_id,
      })
    }

    for (const optionValue of answer as DocumentsQuestionResponseDTO[]) {
      if (optionValue.supplemental) {
        descriptors.push({
          id: optionValue.id,
          title: optionValue.title || 'Additional file',
          documentId: optionValue.document_id,
          supplemental: optionValue.supplemental,
        })
      }
    }

    this.documentRequests.reconcile(descriptors)
  }

  validate(): QuestionValidationResult {
    if (this.validations) {
      const uploadedDocuments =
        this.documentRequests.documentRequestDescriptors.filter(
          (doc) => doc.documentId,
        ).length

      if (uploadedDocuments < this.validations.minDocsRequired) {
        return QuestionValidationResult.failed([
          `At least ${this.validations.minDocsRequired} documents are required`,
        ])
      }
    }

    return QuestionValidationResult.ok()
  }

  dispose() {
    this.onChangeSub.unsubscribe()
  }

  toInputValue(): QuestionInputValue {
    const value = this.documentRequests.documentRequestDescriptors.map((d) => ({
      id: d.id,
      documentId: d.documentId,
    }))
    return { type: 'DOCUMENTS', value }
  }
}

export interface DocumentsQuestionResponse {
  id: string
  documentId: string
  supplemental?: boolean
}

export interface DocumentOption {
  id: string
  text: string
  documentTagIds: string[]
}

export interface DocumentsQuestionViewModelOptions {}
